<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { format, parseISO } from 'date-fns';
import dealReportsAPI from 'dashboard/api/dealReports';
import TableHeaderCell from './TableHeaderCell.vue';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const router = useRouter();
const page = ref(1);
const data = ref(null);
const isLoading = ref(false);
const hasError = ref(false);

const rows = computed(() => data.value?.rows || []);
const totalPages = computed(() => {
  if (!data.value) return 1;
  return Math.max(Math.ceil(data.value.total_count / data.value.per_page), 1);
});

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await dealReportsAPI.getSourceDeals({
      ...props.params,
      page: page.value,
    });
    data.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.params,
  () => {
    page.value = 1;
    fetchData();
  },
  { immediate: true, deep: true }
);
watch(page, fetchData);

const STATUS_KEYS = { regular: 'OPEN', won: 'WON', lost: 'LOST' };

const formatValue = deal =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: deal.currency,
  }).format(deal.value);

const openDeal = dealId => {
  router.push({ name: 'deals_edit', params: { dealId } });
};

const COLUMNS = [
  'NAME',
  'CONTACT',
  'PIPELINE',
  'STAGE',
  'STATUS',
  'ASSIGNEE',
  'UTM_SOURCE',
  'UTM_MEDIUM',
  'UTM_CAMPAIGN',
  'VALUE',
  'CREATED_AT',
];
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.COMERCIAL.SECTIONS.SOURCES') }}
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.COMERCIAL.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.COMERCIAL.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.COMERCIAL.LOADING') }}
    </p>
    <p v-else-if="!rows.length" class="px-3 pb-3 text-sm text-n-slate-10">
      {{ t('DASHBOARD.COMERCIAL.EMPTY_STATE') }}
    </p>
    <div v-else class="px-3 pb-3">
      <div class="overflow-x-auto overflow-y-auto max-h-80">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-10">
              <th v-for="column in COLUMNS" :key="column" class="py-1 pr-3">
                <TableHeaderCell
                  :label="t(`DASHBOARD.COMERCIAL.SOURCES.${column}`)"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="deal in rows"
              :key="deal.id"
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
              @click="openDeal(deal.id)"
            >
              <td class="py-1 pr-3 text-n-slate-12">{{ deal.name }}</td>
              <td class="py-1 pr-3">{{ deal.contact_name || '—' }}</td>
              <td class="py-1 pr-3">{{ deal.pipeline_name }}</td>
              <td class="py-1 pr-3">{{ deal.stage_name }}</td>
              <td class="py-1 pr-3">
                {{
                  t(
                    `DASHBOARD.COMERCIAL.SOURCES.STATUS_${STATUS_KEYS[deal.stage_type]}`
                  )
                }}
              </td>
              <td class="py-1 pr-3">
                {{
                  deal.assigned_to_name ||
                  t('DASHBOARD.COMERCIAL.SOURCES.NO_ASSIGNEE')
                }}
              </td>
              <td class="py-1 pr-3">{{ deal.utm_source || '—' }}</td>
              <td class="py-1 pr-3">{{ deal.utm_medium || '—' }}</td>
              <td class="py-1 pr-3">{{ deal.utm_campaign || '—' }}</td>
              <td class="py-1 pr-3">{{ formatValue(deal) }}</td>
              <td class="py-1 pr-3">
                {{ format(parseISO(deal.created_at), 'dd/MM/yyyy') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between mt-2 text-sm">
        <span class="text-n-slate-11">
          {{
            t('DASHBOARD.COMERCIAL.SOURCES.PAGE_INFO', {
              page: data.page,
              pages: totalPages,
              total: data.total_count,
            })
          }}
        </span>
        <div class="flex gap-1">
          <button
            type="button"
            class="px-3 py-1 rounded-md text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50"
            :disabled="page <= 1"
            @click="page -= 1"
          >
            {{ t('DASHBOARD.COMERCIAL.SOURCES.PREVIOUS') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 rounded-md text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50"
            :disabled="page >= totalPages"
            @click="page += 1"
          >
            {{ t('DASHBOARD.COMERCIAL.SOURCES.NEXT') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
